import { AuthTokens, User } from '../types';

/**
 * Authentication Service Module
 * 
 * Handles all authentication operations with AWS Cognito.
 * Manages OAuth flow, token storage in memory, and token refresh.
 * 
 * Requirements: 7.1, 7.2, 7.3, 7.4
 */

// Get Cognito configuration from environment variables
function getCognitoConfig() {
  return {
    domain: process.env.REACT_APP_COGNITO_DOMAIN || '',
    clientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
    redirectUri: process.env.REACT_APP_REDIRECT_URI || window.location.origin + '/callback',
    logoutUri: process.env.REACT_APP_LOGOUT_URI || window.location.origin,
  };
}

// Token storage using sessionStorage (persists during browser session)
// sessionStorage is cleared when browser tab is closed (more secure than localStorage)
const TOKEN_STORAGE_KEY = 'printflow_auth_tokens';
const USER_STORAGE_KEY = 'printflow_current_user';

let tokenRefreshTimer: NodeJS.Timeout | null = null;

/**
 * Get stored tokens from sessionStorage
 */
function getStoredTokens(): AuthTokens | null {
  try {
    const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    return JSON.parse(stored);
  } catch (error) {
    console.error('[auth] Failed to parse stored tokens:', error);
    return null;
  }
}

/**
 * Get stored user from sessionStorage
 */
function getStoredUser(): User | null {
  try {
    const stored = sessionStorage.getItem(USER_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    return JSON.parse(stored);
  } catch (error) {
    console.error('[auth] Failed to parse stored user:', error);
    return null;
  }
}

/**
 * Store tokens in sessionStorage
 */
function storeTokens(tokens: AuthTokens): void {
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  } catch (error) {
    console.error('[auth] Failed to store tokens:', error);
  }
}

/**
 * Store user in sessionStorage
 */
function storeUser(user: User): void {
  try {
    sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } catch (error) {
    console.error('[auth] Failed to store user:', error);
  }
}

/**
 * Clear stored tokens and user
 */
function clearStorage(): void {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(USER_STORAGE_KEY);
}

/**
 * Login function
 * Redirects user to Cognito hosted UI for authentication
 * 
 * Requirements: 7.1, 7.2
 */
export function login(): void {
  const config = getCognitoConfig();
  
  // Build Cognito hosted UI URL with OAuth parameters
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: config.redirectUri,
  });

  const loginUrl = `https://${config.domain}/oauth2/authorize?${params.toString()}`;
  
  // Redirect to Cognito hosted UI
  window.location.href = loginUrl;
}

/**
 * Logout function
 * Clears session tokens and redirects to logout page
 * 
 * Requirements: 7.1, 7.2
 */
export function logout(): void {
  const config = getCognitoConfig();
  
  // Clear stored tokens and user
  clearStorage();
  
  // Clear token refresh timer
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
  
  // Build Cognito logout URL
  // Cognito hosted UI logout requires 'client_id' and 'logout_uri'.
  // 'redirect_uri' is also needed for some Cognito configurations.
  const logoutRedirect = config.logoutUri || window.location.origin;
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: logoutRedirect,
    redirect_uri: logoutRedirect,
  });

  const logoutUrl = `https://${config.domain}/logout?${params.toString()}`;
  
  // Redirect to Cognito logout
  window.location.href = logoutUrl;
}

/**
 * Get current ID token for API Gateway authorization
 * Returns valid token or null if not authenticated
 * 
 * IMPORTANT: API Gateway Cognito authorizers validate ID tokens, not access tokens.
 * The ID token contains user identity claims and is what API Gateway expects.
 * 
 * Requirements: 7.3, 7.4, 7.5
 * 
 * @returns Current valid ID token or null
 */
export function getToken(): string | null {
  const authTokens = getStoredTokens();
  
  if (!authTokens) {
    return null;
  }
  
  // Check if token is expired
  const now = Date.now();
  
  if (now >= authTokens.expiresAt) {
    // Token expired, clear it
    console.warn('[auth] Token expired, clearing');
    clearStorage();
    return null;
  }
  
  // Decode ID token to check type
  try {
    const parts = authTokens.idToken.split('.');
    if (parts.length === 3) {
      JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    }
  } catch (e) {
    console.error('[auth] Failed to decode ID token:', e);
  }
  
  return authTokens.idToken;
}

/**
 * Get current user information
 * 
 * @returns Current user or null if not authenticated
 */
export function getCurrentUser(): User | null {
  return getStoredUser();
}

/**
 * Check if user is authenticated
 * 
 * @returns True if user has valid token
 */
export function isAuthenticated(): boolean {
  return getToken() !== null;
}

/**
 * Set authentication tokens
 * Called after successful OAuth callback
 * 
 * Requirements: 7.3
 * 
 * @param tokens - Authentication tokens from Cognito
 */
export function setTokens(tokens: AuthTokens): void {
  // Store tokens in sessionStorage
  storeTokens(tokens);
  
  // Decode ID token to extract user information
  const user = decodeIdToken(tokens.idToken);
  storeUser(user);
  
  // Schedule token refresh before expiration
  scheduleTokenRefresh();
}

/**
 * Exchange authorization code for tokens
 * Called from OAuth callback handler
 * 
 * Requirements: 7.2, 7.3
 * 
 * @param code - Authorization code from Cognito
 * @returns Promise with authentication tokens
 */
export async function exchangeCodeForTokens(code: string): Promise<AuthTokens> {
  const config = getCognitoConfig();
  
  // Build token endpoint URL
  const tokenUrl = `https://${config.domain}/oauth2/token`;
  
  // Prepare request body
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code: code,
    redirect_uri: config.redirectUri,
  });
  
  // Request tokens from Cognito
  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });
  
  if (!response.ok) {
    // Try to get error details from response
    let errorMessage = `Failed to exchange authorization code (${response.status})`;
    try {
      const errorData = await response.json();
      console.error('[auth] Token exchange error data:', errorData);
      if (errorData.error) {
        errorMessage = `${errorData.error}: ${errorData.error_description || 'Token exchange failed'}`;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(errorMessage);
  }
  
  const data = await response.json();
  
  // Calculate token expiration time (8 hours from now)
  const expiresAt = Date.now() + (data.expires_in * 1000);
  
  const tokens: AuthTokens = {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    expiresAt,
  };
  
  return tokens;
}

/**
 * Refresh access token using refresh token
 * 
 * Requirements: 7.3, 7.4
 * 
 * @returns Promise with new authentication tokens
 */
export async function refreshToken(): Promise<AuthTokens> {
  const authTokens = getStoredTokens();
  
  if (!authTokens?.refreshToken) {
    throw new Error('No refresh token available');
  }
  
  const config = getCognitoConfig();
  
  // Build token endpoint URL
  const tokenUrl = `https://${config.domain}/oauth2/token`;
  
  // Prepare request body
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.clientId,
    refresh_token: authTokens.refreshToken,
  });
  
  // Request new tokens from Cognito
  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });
  
  if (!response.ok) {
    // Refresh failed, clear tokens and redirect to login
    clearStorage();
    throw new Error('Failed to refresh token');
  }
  
  const data = await response.json();
  
  // Calculate token expiration time
  const expiresAt = Date.now() + (data.expires_in * 1000);
  
  const newTokens: AuthTokens = {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: authTokens.refreshToken, // Keep existing refresh token
    expiresAt,
  };
  
  // Update stored tokens
  setTokens(newTokens);
  
  return newTokens;
}

/**
 * Schedule automatic token refresh before expiration
 * Refreshes token 5 minutes before it expires
 * 
 * Requirements: 7.3, 7.4
 */
function scheduleTokenRefresh(): void {
  // Clear existing timer
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
  }
  
  const authTokens = getStoredTokens();
  
  if (!authTokens) {
    return;
  }
  
  // Calculate time until token expires (refresh 5 minutes before)
  const now = Date.now();
  const timeUntilExpiry = authTokens.expiresAt - now;
  const refreshTime = timeUntilExpiry - (5 * 60 * 1000); // 5 minutes before expiry
  
  // Only schedule if refresh time is in the future
  if (refreshTime > 0) {
    tokenRefreshTimer = setTimeout(async () => {
      try {
        await refreshToken();
      } catch (error) {
        console.error('Failed to refresh token:', error);
        // Redirect to login on refresh failure
        login();
      }
    }, refreshTime);
  }
}

/**
 * Decode JWT ID token to extract user information
 * 
 * @param idToken - JWT ID token from Cognito
 * @returns User information
 */
function decodeIdToken(idToken: string): User {
  try {
    // JWT tokens have three parts separated by dots
    const parts = idToken.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid token format');
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(decodedPayload);
    
    // Extract user information from claims
    const user: User = {
      email: claims.email || '',
      sub: claims.sub || '',
      groups: claims['cognito:groups'] || [],
    };
    
    return user;
  } catch (error) {
    console.error('Failed to decode ID token:', error);
    throw new Error('Invalid ID token');
  }
}

/**
 * Parse OAuth callback URL parameters
 * Extracts authorization code or error from URL
 * 
 * @param url - Callback URL with query parameters
 * @returns Authorization code or null if error
 */
export function parseCallbackUrl(url: string): { code?: string; error?: string } {
  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);
  
  const code = params.get('code');
  const error = params.get('error');
  const errorDescription = params.get('error_description');
  
  if (error) {
    return {
      error: errorDescription || error,
    };
  }
  
  if (code) {
    return { code };
  }
  
  return {};
}

/**
 * Handle OAuth callback
 * Exchanges authorization code for tokens and stores them
 * 
 * Requirements: 7.2, 7.3
 * 
 * @param callbackUrl - Full callback URL with query parameters
 * @returns Promise that resolves when authentication is complete
 */
export async function handleCallback(callbackUrl: string): Promise<void> {
  const { code, error } = parseCallbackUrl(callbackUrl);
  
  if (error) {
    console.error('[auth] OAuth error from Cognito:', error);
    throw new Error(`Authentication failed: ${error}`);
  }
  
  if (!code) {
    console.error('[auth] No authorization code in callback URL');
    throw new Error('No authorization code received');
  }
  
  // Exchange code for tokens
  const tokens = await exchangeCodeForTokens(code);
  
  // Store tokens in memory
  setTokens(tokens);
}

/**
 * Export auth service functions
 */
const authService = {
  login,
  logout,
  getToken,
  getCurrentUser,
  isAuthenticated,
  setTokens,
  exchangeCodeForTokens,
  refreshToken,
  parseCallbackUrl,
  handleCallback,
};

export default authService;
